import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Cinema } from '../models';
import { CinemaService } from '../services/cinema.service';
import { API_BASE } from '../services/api';
import { LocationFilterService, LocationFilterState } from '../services/location-filter.service';

interface LocationOption {
  label: string;
  sublabel: string;
  latitude: number;
  longitude: number;
  weight: number;
  source: 'local' | 'photon';
}

interface PhotonLocationResult {
  label: string;
  sublabel: string;
  latitude: number;
  longitude: number;
  source: 'photon';
}

@Component({
  selector: 'app-location-filter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="location-wrap" *ngIf="state$ | async as state">
      <button class="location-trigger" type="button" (click)="togglePanel()" [class.active]="state.mode !== 'all'" [attr.aria-expanded]="panelOpen">
        <span class="trigger-icon" aria-hidden="true">{{ state.mode === 'all' ? 'All' : 'Near' }}</span>
        <strong>{{ label(state) }}</strong>
      </button>

      <div class="scrim" *ngIf="panelOpen" (click)="closePanel()"></div>
      <section class="location-panel glass" *ngIf="panelOpen" aria-label="Location filter">
        <div class="panel-head">
          <div>
            <h2>Location</h2>
            <p>{{ helperText(state) }}</p>
          </div>
          <button class="close-btn" type="button" (click)="closePanel()">Close</button>
        </div>

        <div class="mode-row">
          <button type="button" [class.active]="state.mode === 'all'" (click)="selectAll()">All Ireland</button>
          <button type="button" [class.active]="state.mode === 'near'" (click)="selectNear()">Near me</button>
        </div>

        <div class="manual-search">
          <label for="location-search">Search place or cinema</label>
          <input id="location-search" class="input" [(ngModel)]="query" placeholder="Dublin, Cork, Liffey Valley..." (ngModelChange)="updateMatches()" />
          <p class="lookup-state" *ngIf="externalLoading">Searching places...</p>
          <p class="lookup-state" *ngIf="!externalLoading && externalError">{{ externalError }}</p>
          <div class="option-list" *ngIf="matches.length">
            <button type="button" *ngFor="let option of matches" (click)="selectOption(option)">
              <span>
                <strong>{{ option.label }}</strong>
                <small>{{ option.sublabel }}<ng-container *ngIf="option.source === 'photon'"> - place result</ng-container></small>
              </span>
              <em>Use</em>
            </button>
          </div>
        </div>

        <div class="radius-block" [class.disabled]="state.mode === 'all'">
          <div class="radius-head">
            <span>Radius</span>
            <strong>{{ state.radiusKm }} km</strong>
          </div>
          <input type="range" min="5" max="500" step="5" [ngModel]="state.radiusKm" (ngModelChange)="setRadius($event)" [disabled]="state.mode === 'all'" />
          <div class="radius-presets">
            <button type="button" *ngFor="let option of radiusOptions" [class.active]="state.radiusKm === option" (click)="setRadius(option)" [disabled]="state.mode === 'all'">{{ option }} km</button>
          </div>
        </div>

        <p class="location-note" *ngIf="state.mode === 'all'">Showing every cinema.</p>
        <p class="location-note" *ngIf="state.mode === 'near' && state.loading">Requesting your current location...</p>
        <p class="location-note success" *ngIf="state.mode === 'near' && !state.loading && state.latitude && state.longitude">Using your live location for this session.</p>
        <p class="location-note success" *ngIf="state.mode === 'manual'">Filtering around {{ state.label }}.</p>
        <p class="location-note error" *ngIf="state.error">{{ state.error }}</p>
        <button class="done-btn" type="button" (click)="closePanel()">Done</button>
      </section>
    </div>
  `,
  styles: [
    `
      .location-wrap { position: relative; }

      .location-trigger {
        min-height: 40px;
        max-width: 210px;
        border: 1px solid var(--line);
        border-radius: 999px;
        padding: 0 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        color: var(--text);
        background: var(--control-bg);
        cursor: pointer;
        font-weight: 950;
      }

      .location-trigger.active {
        border-color: rgba(88, 224, 255, 0.52);
        background: linear-gradient(135deg, rgba(88, 224, 255, 0.18), rgba(140, 93, 255, 0.18));
        box-shadow: 0 10px 30px rgba(88, 224, 255, 0.08);
      }

      .trigger-icon {
        flex: 0 0 auto;
        min-width: 30px;
        min-height: 22px;
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: var(--cyan);
        background: rgba(88, 224, 255, 0.08);
        font-size: 10px;
        font-weight: 950;
        text-transform: uppercase;
      }

      .location-trigger strong {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13px;
      }

      .scrim { display: none; }

      .location-panel {
        position: absolute;
        top: calc(100% + 10px);
        right: 0;
        width: min(390px, calc(100vw - 18px));
        padding: 14px;
        border-radius: 20px;
        box-shadow: 0 26px 80px rgba(0, 0, 0, 0.5);
      }

      .panel-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 12px;
      }

      .panel-head h2 {
        margin: 0 0 3px;
        color: var(--text);
        font-size: 18px;
        line-height: 1.1;
      }

      .panel-head p {
        margin: 0;
        color: var(--muted);
        font-size: 12px;
        font-weight: 750;
      }

      .close-btn {
        min-width: 58px;
        height: 36px;
        border: 1px solid var(--line);
        border-radius: 12px;
        color: var(--text);
        background: var(--control-bg);
        cursor: pointer;
        font-size: 12px;
        font-weight: 950;
      }

      .mode-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }

      .mode-row button,
      .radius-presets button,
      .option-list button {
        border: 1px solid var(--line);
        color: var(--text);
        background: var(--control-bg);
        cursor: pointer;
        font-weight: 900;
      }

      .mode-row button {
        min-height: 44px;
        border-radius: 14px;
      }

      .mode-row button.active,
      .radius-presets button.active {
        border-color: rgba(88, 224, 255, 0.5);
        color: var(--accent-contrast);
        background: var(--cyan);
      }

      .manual-search {
        margin-top: 12px;
        display: grid;
        gap: 8px;
      }

      .manual-search label,
      .radius-head,
      .lookup-state {
        color: var(--muted);
        font-size: 12px;
        font-weight: 900;
      }

      .lookup-state {
        margin: -2px 2px 0;
        font-weight: 750;
      }

      .manual-search .input {
        min-height: 44px;
        border-radius: 14px;
        font-weight: 850;
      }

      .option-list {
        max-height: 230px;
        overflow: auto;
        display: grid;
        gap: 7px;
        padding-right: 2px;
      }

      .option-list button {
        min-height: 54px;
        border-radius: 14px;
        padding: 9px 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        text-align: left;
      }

      .option-list span {
        min-width: 0;
        display: grid;
        gap: 3px;
      }

      .option-list strong {
        color: var(--text);
        font-size: 13px;
        line-height: 1.15;
      }

      .option-list small {
        color: var(--muted);
        font-size: 11px;
        font-weight: 750;
      }

      .option-list em {
        flex: 0 0 auto;
        color: var(--cyan);
        font-size: 12px;
        font-style: normal;
        font-weight: 950;
      }

      .radius-block {
        margin-top: 12px;
        padding: 11px;
        border: 1px solid var(--line);
        border-radius: 16px;
        background: var(--surface-soft);
      }

      .radius-block.disabled { opacity: .52; }

      .radius-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 8px;
      }

      .radius-head strong { color: var(--text); }

      input[type='range'] { width: 100%; accent-color: var(--cyan); }

      .radius-presets {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        margin-top: 9px;
      }

      .radius-presets button {
        min-height: 38px;
        border-radius: 12px;
        font-size: 12px;
      }

      .location-note {
        margin: 10px 2px 0;
        color: var(--muted);
        font-size: 12px;
        font-weight: 750;
        line-height: 1.35;
      }

      .location-note.success { color: var(--green); }
      .location-note.error { color: var(--danger); }
      .done-btn { display: none; }

      @media (max-width: 767px) {
        .location-trigger {
          min-height: 42px;
          max-width: 138px;
          border-radius: 14px;
          padding: 0 10px;
        }

        .location-trigger strong { font-size: 12px; }

        .scrim {
          display: block;
          position: fixed;
          inset: 0;
          z-index: 55;
          background: rgba(0, 0, 0, 0.54);
          backdrop-filter: blur(4px);
        }

        .location-panel {
          position: fixed;
          left: 10px;
          right: 10px;
          top: 74px;
          bottom: calc(76px + env(safe-area-inset-bottom));
          height: calc(100dvh - 150px - env(safe-area-inset-bottom));
          z-index: 70;
          width: auto;
          max-height: none;
          overflow: auto;
          border-radius: 20px 20px 16px 16px;
          padding: 13px 13px 14px;
          overscroll-behavior: contain;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .panel-head {
          position: sticky;
          top: -13px;
          z-index: 2;
          margin: -13px -13px 0;
          padding: 13px;
          border-bottom: 1px solid var(--line);
          background: var(--surface-elevated);
          backdrop-filter: blur(16px);
          border-radius: 20px 20px 0 0;
        }

        .panel-head h2 { font-size: 17px; }
        .mode-row button,
        .manual-search .input { min-height: 46px; }
        .manual-search,
        .radius-block { margin-top: 0; }
        .option-list { max-height: min(230px, 28vh); }
        .radius-presets button { min-height: 42px; }
        .location-note { margin-top: 0; }

        .done-btn {
          display: block;
          position: sticky;
          bottom: -14px;
          min-height: 48px;
          margin: 0 -13px -14px;
          border: 0;
          border-top: 1px solid var(--line);
          color: var(--accent-contrast);
          background: var(--cyan);
          font-weight: 950;
          cursor: pointer;
        }
      }

      @media (max-width: 390px) {
        .location-trigger { max-width: 120px; padding: 0 8px; }
        .location-panel {
          left: 8px;
          right: 8px;
          top: 72px;
          bottom: calc(74px + env(safe-area-inset-bottom));
          height: calc(100dvh - 146px - env(safe-area-inset-bottom));
        }
        .radius-presets { grid-template-columns: repeat(2, 1fr); }
      }
    `,
  ],
})
export class LocationFilterComponent implements OnInit {
  readonly state$ = this.location.state$;
  readonly radiusOptions = [10, 25, 50, 100, 250, 500];
  panelOpen = false;
  query = '';
  matches: LocationOption[] = [];
  externalLoading = false;
  externalError = '';
  private options: LocationOption[] = [];
  private lookupTimer?: ReturnType<typeof setTimeout>;
  private lookupToken = 0;

  constructor(
    private readonly location: LocationFilterService,
    private readonly cinemas: CinemaService,
    private readonly http: HttpClient,
  ) {}

  ngOnInit() {
    this.cinemas.list().subscribe((cinemas) => {
      this.options = this.buildOptions(cinemas);
      this.updateMatches();
    });
  }

  @HostListener('document:keydown.escape')
  closePanel() {
    this.panelOpen = false;
  }

  togglePanel() {
    this.panelOpen = !this.panelOpen;
    if (this.panelOpen) this.updateMatches();
  }

  selectAll() {
    this.location.useAllIreland();
  }

  selectNear() {
    this.location.useNearMe();
  }

  selectOption(option: LocationOption) {
    this.location.useManualLocation(option.label, option.latitude, option.longitude);
    this.query = option.label;
    this.panelOpen = false;
  }

  setRadius(value: number | string) {
    this.location.setRadius(Number(value));
  }

  updateMatches() {
    const term = this.query.trim().toLowerCase();
    const ranked = this.options
      .filter((option) => !term || `${option.label} ${option.sublabel}`.toLowerCase().includes(term))
      .sort((a, b) => b.weight - a.weight || a.label.localeCompare(b.label));
    this.matches = ranked.slice(0, term ? 10 : 8);
    this.externalError = '';

    if (this.lookupTimer) clearTimeout(this.lookupTimer);
    if (term.length < 3 || this.matches.length > 0) {
      this.externalLoading = false;
      return;
    }

    this.externalLoading = true;
    this.lookupTimer = setTimeout(() => this.lookupExternal(term), 650);
  }

  label(state: LocationFilterState): string {
    if (state.mode === 'near') {
      if (state.loading) return 'Locating...';
      return `Near me - ${state.radiusKm} km`;
    }
    if (state.mode === 'manual') return `${state.label || 'Place'} - ${state.radiusKm} km`;
    return 'All Ireland';
  }

  helperText(state: LocationFilterState): string {
    if (state.mode === 'manual') return `Around ${state.label}, within ${state.radiusKm} km`;
    if (state.mode === 'near') return `Around your live location, within ${state.radiusKm} km`;
    return 'Choose a place or use live location.';
  }

  private buildOptions(cinemas: Cinema[]): LocationOption[] {
    const withCoords = cinemas.filter((cinema) => typeof cinema.latitude === 'number' && typeof cinema.longitude === 'number');
    const options: LocationOption[] = withCoords.map((cinema) => ({
      label: cinema.name,
      sublabel: `${cinema.city}, ${cinema.county}`,
      latitude: cinema.latitude!,
      longitude: cinema.longitude!,
      weight: 1,
      source: 'local',
    }));

    const placeMap = new Map<string, Cinema[]>();
    withCoords.forEach((cinema) => {
      const key = `${cinema.city}, ${cinema.county}`;
      placeMap.set(key, [...(placeMap.get(key) ?? []), cinema]);
    });

    placeMap.forEach((items, label) => {
      options.push({
        label,
        sublabel: `${items.length} cinema${items.length === 1 ? '' : 's'}`,
        latitude: this.average(items.map((item) => item.latitude!)),
        longitude: this.average(items.map((item) => item.longitude!)),
        weight: items.length + 10,
        source: 'local',
      });
    });

    return options;
  }

  private average(values: number[]): number {
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  private lookupExternal(term: string) {
    const token = ++this.lookupToken;
    this.http.get<PhotonLocationResult[]>(`${API_BASE}/location/search`, { params: { q: term } }).subscribe({
      next: (results) => {
        if (token !== this.lookupToken) return;
        this.externalLoading = false;
        this.matches = results.map((result) => ({
          ...result,
          weight: 0,
        }));
        this.externalError = this.matches.length ? '' : 'No place matches found.';
      },
      error: () => {
        if (token !== this.lookupToken) return;
        this.externalLoading = false;
        this.externalError = 'Place search is unavailable right now.';
      },
    });
  }
}
